<template>
  <div class="sub-agent-event-root">
    <div class="custom-nav-block" :class="[scrollTop ? 'nav-scrolling' : 'nav-default']" :style="`padding-top:${topBarHeight}px`">
      <van-nav-bar class="van-bar" title="" left-arrow @click-left="goPrevious">
        <template #left>
          <img class="left-nav" src="@/assets/packageAssets/common/arrow01.webp" alt="" />
        </template>
      </van-nav-bar>
    </div>

    <div class="event-head">
      <img class="hero-bg" src="./images/hero-bg.webp" alt="" />
      <div class="head-content" :style="{ paddingTop: `${topBarHeight + 46}px` }">
        <h1 class="event-title">{{ $t('lang.SubAgentEvent.k001') }}</h1>
        <div class="event-desc">
          <p>{{ $t('lang.SubAgentEvent.k002') }}</p>
          <p>{{ $t('lang.SubAgentEvent.k003') }}</p>
        </div>
        <div class="event-time-pill">
          <span>{{ $t('lang.SubAgentEvent.k004') }}</span>
          <span>{{ eventTimeText }}</span>
        </div>
        <button type="button" class="rules-entry" @click="handleToRules">
          <img class="rules-entry-icon" src="@/views/P5_Activitys/SubAgentEvent/images/rules.webp" alt="" />
          <span class="rules-entry-text">{{ $t('lang.SubAgentEvent.k014') }}</span>
        </button>
      </div>
    </div>

    <div class="event-body">
      <div class="time-tab-wrap">
        <div ref="timeTabList" class="time-switch-tab">
          <div
            v-for="(tab, index) in timeTabs"
            :key="tab.key"
            :ref="`timeTab-${index}`"
            class="time-tab"
            :class="{ 'active-tab': timeTabIndex === index }"
            @click="handleTimeTab(index)"
          >
            {{ tab.label }}
          </div>
        </div>
      </div>

      <div class="main-card">
        <div class="bonus-card">
          <span v-if="weekData.settled" class="settled-tag">{{ $t('lang.SubAgentEvent.k005') }}</span>
          <div class="bonus-value">{{ thousandthDigit(weekData.unlockedBonus) }}</div>
          <div class="bonus-label">{{ $t('lang.SubAgentEvent.k006') }}</div>
        </div>

        <div class="hostess-row">
          <span class="hostess-label">{{ $t('lang.SubAgentEvent.k007') }}</span>
          <span class="hostess-value">{{ weekData.verifiedHostesses }}</span>
        </div>

        <div class="progress-block">
          <PercentBar :percentCount="weekData.percentCount" :earnTypeList="weekData.earnTypeList" />
        </div>

        <div v-if="showUnlockTips" class="unlock-tips">
          <MyPLabel class="unlock-main" :str="$t(unlockTipLangKey)" :numsOption="unlockTipNumsOption" />
        </div>
        <button type="button" class="invite-btn" @click="handleMainAction">{{ $t(inviteBtnLangKey) }}</button>
      </div>

      <SubAgentNoticeBar :notices="notices" />

      <div v-if="leaderboard && leaderboard.length > 0" class="leaderboard-card">
        <div class="leaderboard-head">{{ $t('lang.SubAgentEvent.k009') }}</div>
        <div class="leaderboard-table">
          <div class="leaderboard-header">
            <div class="th th-agent">{{ $t('lang.SubAgentEvent.k010') }}</div>
            <div class="th th-invites">{{ $t('lang.SubAgentEvent.k011') }}</div>
          </div>
          <div v-for="item in leaderboard" :key="item.rank" class="leaderboard-row">
            <div class="td td-agent">
              <span class="rank-badge" :class="{ 'rank-top': item.rank <= 3 }" :style="rankBadgeStyle(item.rank)">
                {{ item.rank }}
              </span>
              <div class="agent-info">
                <img class="avatar" :src="item.avatar | imgPath" alt="" @error="handleAvatarError($event)" />
                <span class="nickname">{{ item.nickname }}</span>
              </div>
            </div>
            <div class="td td-invites">{{ item.invites }}</div>
          </div>
          <div v-if="!leaderboard.length" class="leaderboard-empty"></div>
        </div>
      </div>

      <div :style="{ height: `${bottomBarHeight}px` }"></div>
    </div>
  </div>
</template>

<script>
import { apiFetchInviteV2HomePage } from '@/api/invite'
import { apiFetchSubAgentCarousel, apiFetchSubAgentWeekAct } from './apidir/api'
import { formatEventTimeRange, getWeekActWeekDiff, mapCarouselList, mapSubAgentRankList, mapWeekActResponse, RANK_COLORS } from './utils/index'

import PercentBar from './components/PercentBar.vue'
import SubAgentNoticeBar from './components/NoticeBar.vue'
import carouselPollMixin from './hooks/carousel-poll.mixin.js'
import scrollMixin from '@/hooks/scroll.mixin.js'

const DEFAULT_WEEK_DATA = {
  verifiedHostesses: 0,
  unlockedBonus: 0,
  settled: false,
  percentCount: 0,
  remainingToUnlock: 0,
  nextTierRewardBean: 0,
  earnTypeList: [],
  actStart: 0,
  actEnd: 0,
}

export default {
  name: 'SubAgentEvent',
  components: { PercentBar, SubAgentNoticeBar },
  mixins: [scrollMixin, carouselPollMixin],
  data() {
    return {
      timeTabIndex: 0,
      leaderboard: [],
      weekData: { ...DEFAULT_WEEK_DATA },
      eventTimeText: '',
      inviteCode: '',
      loading: false,
      notices: [],
    }
  },
  computed: {
    isHistoryWeekTab() {
      return this.timeTabIndex > 0
    },
    unlockTipLangKey() {
      return this.isHistoryWeekTab ? 'lang.SubAgentEvent.k012' : 'lang.SubAgentEvent.k008'
    },
    inviteBtnLangKey() {
      return this.isHistoryWeekTab ? 'lang.SubAgentEvent.k013' : 'lang.Invite.k101'
    },
    showUnlockTips() {
      if (this.isHistoryWeekTab) return true
      return this.weekData.remainingToUnlock > 0
    },
    unlockCountOption() {
      return [
        { value: this.weekData.remainingToUnlock, class: 'unlock-count' },
        { value: this.thousandthDigit(this.weekData.nextTierRewardBean), class: 'unlock-count' },
      ]
    },
    unlockTipNumsOption() {
      if (this.isHistoryWeekTab) {
        return []
      }
      return this.unlockCountOption
    },
    timeTabs() {
      return [
        { key: 'thisWeek', label: this.$t('lang.BaseCommon.k034') },
        { key: 'lastWeek', label: this.$t('lang.BaseCommon.k042') },
        { key: 'twoWeeksAgo', label: this.$t('lang.Invite.k115') },
        { key: 'threeWeeksAgo', label: this.$t('lang.Invite.k116') },
      ]
    },
  },
  created() {
    this.fetchWeekAct()
    this.startCarouselPoll()
  },
  methods: {
    loadCarouselNotices() {
      apiFetchSubAgentCarousel().then((res) => {
        this.notices = mapCarouselList((res && res.list) || [])
      })
    },
    fetchWeekAct() {
      if (this.loading) return
      this.loading = true
      apiFetchSubAgentWeekAct({
        weekDiff: getWeekActWeekDiff(this.timeTabIndex),
      })
        .then((res) => {
          this.weekData = mapWeekActResponse(res)
          this.leaderboard = mapSubAgentRankList(res?.subAgentList)
          this.eventTimeText = formatEventTimeRange(this.weekData.actStart, this.weekData.actEnd, this.handleMoment.bind(this))
        })
        .finally(() => {
          this.loading = false
        })
    },
    handleAvatarError(event) {
      event.target.src = require('./images/avatar-default.webp')
    },
    rankBadgeStyle(rank) {
      if (RANK_COLORS[rank]) {
        return { background: RANK_COLORS[rank] }
      }
      return {}
    },
    handleMainAction() {
      if (this.isHistoryWeekTab) {
        this.handleTimeTab(0)
        return
      }
      this.handleInviteSubAgent()
    },
    handleToRules() {
      this.$router.push({ path: '/invite-rules', query: { location: 'subAgentEventBonus' } })
    },
    handleInviteSubAgent() {
      if (this.inviteCode) {
        this.shareInviteSubAgent()
        return
      }
      apiFetchInviteV2HomePage().then((res) => {
        this.inviteCode = (res && res.inviteCode) || ''
        this.shareInviteSubAgent()
      })
    },
    getShareTitle() {
      const shareTitleMap = {
        echo: 'Echo - Live video chat',
        joychat: 'Joychat - Live video chat',
        joyu: 'Joyu - Live video chat',
        kiko: 'Kiko - Live video chat',
        vava: 'Vava - Live video chat',
        vibeo: 'Vibeo - Live video chat',
        whonow: 'WhoNow - Live video chat',
      }
      return shareTitleMap[this.packageName] || shareTitleMap.vava
    },
    shareInviteSubAgent() {
      const lang = this.$client.getAppInfoLang()
      const address = this.getMatchAddress()
      const appName = this.getAppName() || 'com.client.goparty'
      const enc = encodeURIComponent
      const query = `lang=${enc(lang)}&code=${enc(this.inviteCode || '')}&appName=${enc(appName)}`
      const url = `https://${address}/download_agent/index.html?${query}`
      const des = this.$t('lang.Invite.k002') || "I've made 10000 beans (worth $10) here by answering calls, join me now!"

      this.$client.copyLink(`${des} # ${url}`, this.$t('lang.BaseCommon.k071'))

      try {
        window.ta && window.ta.track('agent_center', { Action: 'Click', APosition: 'button', AValue: 'InviteSubAgent' })
      } catch (err) {
        console.log('try catch', err)
      }

      try {
        shareIntent.postMessage(
          JSON.stringify({
            title: this.getShareTitle(),
            description: des,
            url,
          })
        )
      } catch (err) {
        console.log('try catch', err)
      }
    },
    handleTimeTab(index) {
      if (this.timeTabIndex === index) return
      this.timeTabIndex = index
      this.loading = false
      this.fetchWeekAct()
      this.$nextTick(() => {
        const tabList = this.$refs.timeTabList
        const tabRef = this.$refs[`timeTab-${index}`]
        const tabEl = Array.isArray(tabRef) ? tabRef[0] : tabRef
        if (tabList && tabEl) {
          const listRect = tabList.getBoundingClientRect()
          const tabRect = tabEl.getBoundingClientRect()
          const offset = tabRect.left - listRect.left - (listRect.width - tabRect.width) / 2
          tabList.scrollTo({ left: tabList.scrollLeft + offset, behavior: 'smooth' })
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.sub-agent-event-root {
  width: 100%;
  min-height: 100vh;
  background: #e8e1ed;

  .custom-nav-block {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 100;
    width: 100%;

    &.nav-default {
      background: transparent;
    }

    &.nav-scrolling {
      background: #ddbff2;
    }

    .van-bar {
      background: transparent;
    }
  }

  .event-head {
    position: relative;
    width: 100%;

    .hero-bg {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 375px;
      object-fit: cover;
      pointer-events: none;
    }

    .head-content {
      position: relative;
      z-index: 1;
      padding: 0 16px 20px;

      .event-title {
        margin: 0 0 8px;
        font-family: 'PlusJakartaSans-Regular', var(--string-font);
        font-weight: var(--fw-700);
        font-style: italic;
        font-size: 28px;
        line-height: 36px;
        color: #ffffff;
      }

      .event-desc {
        padding-right: 52px;
        margin-bottom: 8px;

        p {
          margin: 0;
          font-family: 'PlusJakartaSans-Regular', var(--string-font);
          font-weight: var(--fw-700);
          font-style: italic;
          font-size: var(--fs-12);
          line-height: var(--lh-body-18);
          color: #544091;
        }
      }

      .event-time-pill {
        display: inline-flex;
        align-items: center;
        padding: 3px 8px;
        background: rgba(0, 0, 0, 0.2);
        border-radius: 20px;
        font-family: var(--string-font);
        font-style: italic;
        font-size: var(--fs-12);
        line-height: var(--lh-label-16);
        color: #ffffff;
        white-space: nowrap;

        > * + * {
          margin-left: 4px;
        }
      }

      .rules-entry {
        position: absolute;
        right: 0;
        bottom: 20px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 10px 8px;
        border: none;
        border-top: 1px solid #ffffff;
        border-left: 1px solid #ffffff;
        border-radius: 8px 0 0 8px;
        background: linear-gradient(90deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0.4) 100%);
        cursor: pointer;

        > * + * {
          margin-left: 2px;
        }

        .rules-entry-icon {
          width: 16px;
          height: 16px;
          flex-shrink: 0;
        }

        .rules-entry-text {
          font-family: var(--string-font);
          font-size: var(--fs-12);
          line-height: var(--lh-title-20);
          color: #544091;
          white-space: nowrap;
        }
      }
    }
  }

  .event-body {
    position: relative;
    z-index: 1;
    padding: 12px 16px 24px;
    display: flex;
    flex-direction: column;

    > * + * {
      margin-top: 16px;
    }

    .time-tab-wrap {
      width: 100%;
      overflow: hidden;

      .time-switch-tab {
        display: flex;
        align-items: center;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;

        > * + * {
          margin-left: 8px;
        }

        &::-webkit-scrollbar {
          display: none;
        }

        .time-tab {
          flex-shrink: 0;
          padding: 8px 16px;
          border-radius: 36px;
          background: rgba(255, 255, 255, 0.5);
          font-family: var(--string-font);
          font-size: var(--fs-14);
          line-height: var(--lh-title-20);
          color: #724c8b;
          white-space: nowrap;
          text-align: center;

          &.active-tab {
            background: #b94cff;
            color: #ffffff;
          }
        }
      }
    }

    .main-card {
      display: flex;
      flex-direction: column;
      padding: 12px;
      background: linear-gradient(180deg, #e7c6fc 0%, #ffffff 100%);
      border-radius: 16px;

      > * + * {
        margin-top: 10px;
      }

      .bonus-card {
        position: relative;
        width: 100%;
        min-height: 86px;
        padding: 12px 14px;
        background: rgba(255, 255, 255, 0.5);
        border-radius: 12px;
        display: flex;
        flex-direction: column;
        box-sizing: border-box;

        > * + * {
          margin-top: 4px;
        }

        .settled-tag {
          position: absolute;
          top: 0;
          left: 0;
          padding: 1.5px 8px 0.5px;
          background: #c4fbff;
          border-bottom: 0.5px solid #ffffff;
          border-right: 0.5px solid #ffffff;
          border-radius: 8px 0 8px 0;
          font-family: var(--string-font);
          font-size: 10px;
          line-height: 14px;
          color: #436f72;
        }

        .bonus-value {
          font-family: var(--number-font);
          font-style: italic;
          font-weight: var(--fw-500);
          font-size: 24px;
          line-height: 32px;
          color: #681a9a;
        }

        .bonus-label {
          font-family: var(--string-font);
          font-size: var(--fs-12);
          line-height: var(--lh-body-18);
          color: #9c7cb0;
        }
      }

      .hostess-row {
        display: flex;
        align-items: flex-start;

        > * + * {
          margin-left: 4px;
        }

        .hostess-label {
          font-family: var(--string-font);
          font-size: var(--fs-12);
          line-height: var(--lh-body-18);
          color: #9c7cb0;
          white-space: nowrap;
        }

        .hostess-value {
          font-family: var(--number-font);
          font-style: italic;
          font-size: var(--fs-14);
          line-height: var(--lh-body-18);
          color: #681a9a;
          white-space: nowrap;
        }
      }

      .progress-block {
        padding: 8px 0 4px;
      }

      .unlock-tips {
        text-align: center;

        .unlock-main {
          margin: 0;
          font-family: var(--string-font);
          font-size: var(--fs-14);
          line-height: var(--lh-title-20);
          color: #333333;

          :deep(.unlock-count) {
            font-family: var(--number-font);
            font-style: italic;
            font-weight: var(--fw-500);
            font-size: 20px;
            line-height: 28px;
            color: #333333;
          }
        }
      }

      .invite-btn {
        width: 100%;
        padding: 12px 24px;
        border: 1px solid #f8ddfe;
        border-radius: 44px;
        background: linear-gradient(180deg, #e9c0f7 0%, #c76df5 32.69%, #7012f5 100%);
        box-shadow: inset 0 -3px 6px 0 #f2bbfb;
        font-family: var(--string-font);
        font-size: var(--fs-16);
        line-height: var(--lh-title-20);
        color: #ffffff;
        white-space: nowrap;
      }
    }

    .leaderboard-card {
      background: linear-gradient(180deg, #e7c6fc 0%, #ffffff 100%);
      border-radius: 16px;
      overflow: hidden;

      .leaderboard-head {
        padding: 10px;
        background: #b94cff;
        border-radius: 12px 12px 0 0;
        font-family: var(--string-font);
        font-size: var(--fs-16);
        line-height: var(--lh-title-20);
        color: #ffffff;
        text-align: center;
      }

      .leaderboard-table {
        background: #f3e9fa;
        border-radius: 0 0 12px 12px;
        overflow: hidden;

        .leaderboard-header {
          display: grid;
          grid-template-columns: 1fr 166px;

          .th {
            padding: 10px;
            background: #ddbff2;
            font-family: var(--string-font);
            font-size: var(--fs-14);
            line-height: var(--lh-title-20);
            color: #544091;
            text-align: center;
          }

          .th-agent {
            text-align: center;
          }
        }

        .leaderboard-empty {
          min-height: 120px;
          background: #f3e9fa;
        }

        .leaderboard-row {
          display: grid;
          grid-template-columns: 1fr 166px;
          border-top: 1px solid rgba(255, 255, 255, 0.5);

          .td-agent {
            display: flex;
            align-items: center;
            padding: 14px 16px;
            min-width: 0;

            > * + * {
              margin-left: 12px;
            }

            .rank-badge {
              flex-shrink: 0;
              display: flex;
              align-items: center;
              justify-content: center;
              width: 20px;
              height: 20px;
              border-radius: 22px;
              background: rgba(255, 255, 255, 0.2);
              font-family: var(--number-font);
              font-size: var(--fs-14);
              line-height: 18px;
              color: #333333;
            }

            .agent-info {
              display: flex;
              align-items: center;
              min-width: 0;

              > * + * {
                margin-left: 8px;
              }

              .avatar {
                flex-shrink: 0;
                width: 24px;
                height: 24px;
                border-radius: 50%;
                object-fit: cover;
              }

              .nickname {
                flex: 1;
                min-width: 0;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                font-family: var(--string-font);
                font-size: var(--fs-14);
                line-height: var(--lh-title-20);
                color: #333333;
              }
            }
          }

          .td-invites {
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 6px 10px;
            font-family: var(--string-font);
            font-size: var(--fs-16);
            line-height: 22px;
            color: #333333;
            text-align: center;
          }
        }
      }
    }
  }
}
</style>

<style lang="scss">
html[lang='ar'],
html[lang='fa'],
html[lang='he'] {
  .sub-agent-event-root {
    .event-head {
      .head-content {
        .event-desc {
          padding-right: 0;
          padding-left: 52px;
        }

        .event-time-pill {
          > * + * {
            margin-left: 0;
            margin-right: 4px;
          }
        }

        .rules-entry {
          right: auto;
          left: 0;
          border-left: none;
          border-right: 1px solid #ffffff;
          border-radius: 0 8px 8px 0;

          > * + * {
            margin-left: 0;
            margin-right: 2px;
          }
        }
      }
    }

    .event-body {
      .time-tab-wrap .time-switch-tab {
        > * + * {
          margin-left: 0;
          margin-right: 8px;
        }
      }

      .main-card {
        .hostess-row {
          > * + * {
            margin-left: 0;
            margin-right: 4px;
          }
        }
      }

      .leaderboard-card {
        .leaderboard-table .leaderboard-row .td-agent {
          > * + * {
            margin-left: 0;
            margin-right: 12px;
          }

          .agent-info {
            > * + * {
              margin-left: 0;
              margin-right: 8px;
            }
          }
        }
      }
    }
  }
}
</style>
